import {ApiClient} from '@hconnect/apiclient'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {useQueryClient, useMutation, type UseMutationOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useTranslation} from 'react-i18next'

import {editCommentPath} from '../api'

type Props = {
  plantId: string
  commentsCategory: string
}

export type EditCommentParameter = {
  value: string
  id: string
  commentCategoryInstanceId: string
}

export const useEditComment = (
  {plantId, commentsCategory}: Props,
  queryKeyPrefix: string = 'comments',
  options?: Omit<UseMutationOptions<void, AxiosError, EditCommentParameter>, 'mutationFn'>
) => {
  const {t} = useTranslation()
  const client = useQueryClient()

  return useMutation<void, AxiosError, EditCommentParameter>({
    ...options,
    mutationFn: async ({value, id, commentCategoryInstanceId}) => {
      const payload = {value}
      const path = editCommentPath({
        plantId,
        commentsCategory,
        commentCategoryInstanceId,
        commentId: id
      })

      await ApiClient.axiosInstance.put(path, payload)
    },
    onSuccess: (...args) => {
      spawnSuccessToast(t('comments.message.editSuccess'))
      void client.invalidateQueries({queryKey: [queryKeyPrefix]})
      options?.onSuccess?.(...args)
    },
    onError: (...args) => {
      spawnErrorToast(t('comments.message.failedToUpdateComment'))
      options?.onError?.(...args)
    },
    onSettled: (...args) => {
      options?.onSettled?.(...args)
    }
  })
}
